import React from "react";
import { useLocation } from "react-router-dom";
import "./Layout.scss";
import { Footer } from "../Footer/Footer";
import { useRouteChecks } from "../../hooks/useRouteChecks";
import { Header } from "../Header/Header";
import { ScrollToTop } from "../__common/ScrollToTop/ScrollToTop";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { EuCookieAlert, NonEuCookieAlert } from "../CookieModal/CookieModal";
import { HelpTipsPoupup } from "../HelpTipsPopup/HelpTipsPopup";
import { ROUTES } from "../../configs/routes";
import classNames from "classnames";
import { useLoginOptionHooks } from "../../hooks/login-option-hooks";
import { LoginOptionState } from "../../store/reducers/login-option-reducer";
import { IdleTimeoutDialog } from "../__common/dialog/IdleTimeoutDialog/idleTimeoutDialog";
import { useAuthRouteLoginHandler } from "../../hooks/auth-login-route-hooks";
import { useEmailLoginHooks } from "../../hooks/email-login-hooks";
import { useLayoutStartup } from "../../hooks/layout-startup-hooks";
import { UserState } from "../../store/reducers/user-reducer";
import { AuthModals } from "./AuthModals";
import { SHOW_TIMEOUT_DIALOG } from "../../constants/storage";
import { LogoutLoading } from "../LogoutLoading/LogoutLoading";
interface Props {
  children: React.ReactNode;
}

export const Layout: React.FC<Props> = (props) => {
  // third party hooks
  const { pathname } = useLocation();
  // custom hooks
  const { toggleLoginModal, toggleEmailLoginModal, toggleEmailOtpModal } =
    useLoginOptionHooks();

  const { shouldShowCookieNotice, shouldShowHeaderFooter, shouldShowHelpTips } =
    useRouteChecks(pathname);

  // redux states
  const { isLoginModalOpen, isEmailLoginModalOpen, isEmailOtpModalOpen } =
    useSelector<ApplicationState, LoginOptionState>(
      (state) => state.loginOption
    );
  const { isGeoIPLoading } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const { isLoggedIn, isLoggingOut } = useSelector<
    ApplicationState,
    { isLoggedIn: boolean; isLoggingOut: boolean }
  >((state) => state.auth);

  // variables
  const isHomePage = pathname === ROUTES.ROOT;
  const timeOutDialog = localStorage.getItem(SHOW_TIMEOUT_DIALOG);
  const isTimeOutDialogOpen = timeOutDialog === "true";
  const isMaintenancePage = pathname === ROUTES.ERROR_MAINTENANCE;

  // THIS WILL ALSO INVOKE OTHER LAYOUT RENDER CONDITION AND EFFECTS LIKE REDIRECTION SCENARIOS AND ETC.
  const { hasPageAccess } = useLayoutStartup();
  // FOR EMAIL LOGIN SCENARIOS
  useEmailLoginHooks();
  // FOR HANDLING DEEPLINK OF AUTH ROUTES PAGES AND DISPLAYING EITHER LOGIN MODAL OR NORMAL SA LOGIN
  useAuthRouteLoginHandler();
  // RETURN NO ACCESS DIV WHEN USER HAS NO ACCESS TO LAYOUT DISPLAY (when user countrycode is CN and I8next is not initialized)
  if (!hasPageAccess) {
    return <div data-testid="No-access"></div>;
  }

  // MAIN LAYOUT
  return (
    <div className="body" data-testid="layout">
      {/* Full-screen loading overlay while logging out */}
      <LogoutLoading isLoggingOut={isLoggingOut} />
      {/* Dialog Modal */}
      {<IdleTimeoutDialog isTimeOutDialogOpen={isTimeOutDialogOpen} />}
      {/* Auth related modal container */}
      <AuthModals
        isLoginModalOpen={!isLoggedIn && isLoginModalOpen}
        isEmailLoginModalOpen={isEmailLoginModalOpen ?? false}
        isEmailOtpModalOpen={isEmailOtpModalOpen ?? false}
        onLoginModalClose={() => toggleLoginModal({ isLoginModalOpen: false })}
        onEmailLoginModalClose={toggleEmailLoginModal}
        onOtpModalClose={toggleEmailOtpModal}
      />
      {/* Cookie Notice */}
      {!isGeoIPLoading && shouldShowCookieNotice && (
        <>
          <NonEuCookieAlert />
          <EuCookieAlert />
        </>
      )}
      {/* Main Body */}
      {shouldShowHeaderFooter && (
        <Header isMaintenanceMode={isMaintenancePage} />
      )}
      <main
        id="main-body"
        className={classNames({
          "snap-scroll": isHomePage,
        })}
      >
        {/* TO DO: use reactPortal for rendering page popup to centralize its positioning */}
        {shouldShowHelpTips && <HelpTipsPoupup />}
        <div className="layout" id="layout-container">
          {props.children}
        </div>
        <ScrollToTop />
        {shouldShowHeaderFooter && !isMaintenancePage && <Footer />}
      </main>
    </div>
  );
};
